import type {
  ActionItem,
  CustomAnswer,
  InfographicImage,
  LiveDiagram,
  MeetingSummary,
  Screenshot,
  UpsellOpportunity,
} from "@scoach/types";
import { langMeta, resolveCustomQuestions } from "@scoach/types";
import { useToast } from "@scoach/ui";
import {
  Alert,
  Check,
  Chev,
  Close,
  Expand,
  Globe,
  Image,
  Monitor,
  Plus,
  Settings,
  Spark,
  Trend,
  Users,
} from "@scoach/ui/icons";

import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";

import { AuthImage } from "../../../components/AuthImage.tsx";
import { DictateButton } from "../../../components/DictateButton.tsx";
import { ReadAloudButton } from "../../../components/ReadAloudButton.tsx";
import { ensureGeminiKey, hasGeminiKey } from "../../../lib/geminiKey.ts";
import { safeHref } from "../../../lib/safeHref.ts";
import { authApi } from "../../auth/api.ts";
import { useAuthStore } from "../../auth/store.ts";
import { MermaidDiagram } from "../../meeting/live/components/diagrams/MermaidDiagram.tsx";
import { openDiagramInNewTab } from "../../meeting/live/components/diagrams/openDiagramInNewTab.ts";
import { CustomQuestionsEditor } from "../../settings/components/CustomQuestionsEditor.tsx";
import { summaryApi } from "../api.ts";
import { MeetingChat } from "./MeetingChat.tsx";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : (import.meta.env.VITE_API_BASE_URL as string);

/** Collapse duplicate participant strings (e.g. "John Smith" and "John Smith (CE)"
 * or repeated names) by their name portion. Guards existing summaries whose stored
 * participant list was captured before backend dedup. Mirrors the server-side
 * dedupeParticipants fuzzy match (token containment + Jaccard). */
function dedupeParticipantStrings(list: string[]): string[] {
  const norm = (s: string) =>
    s
      .replace(/\s*\([^)]*\)\s*$/, "") // drop trailing "(role)"
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const same = (a: string, b: string) => {
    const na = norm(a);
    const nb = norm(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    const ta = new Set(na.split(" ").filter(Boolean));
    const tb = new Set(nb.split(" ").filter(Boolean));
    const [small, large] = ta.size <= tb.size ? [ta, tb] : [tb, ta];
    if (small.size > 0 && [...small].every((t) => large.has(t))) return true;
    const union = new Set([...ta, ...tb]).size;
    const inter = [...ta].filter((t) => tb.has(t)).length;
    return union > 0 && inter / union >= 0.6;
  };
  const out: string[] = [];
  for (const p of list) {
    if (!p.trim()) continue;
    const idx = out.findIndex((q) => same(q, p));
    if (idx === -1) out.push(p);
    else if (p.length > out[idx]!.length) out[idx] = p; // keep the richer variant
  }
  return collapseGarbledFirstNames(out);
}

/** Collapse 3+ same-first-name variants (usually one person's mis-transcribed /
 * OCR-garbled name captured many times) into a single entry with the most common
 * surname. Groups of 1-2 are left as-is so two real same-first-name people stay. */
function collapseGarbledFirstNames(list: string[]): string[] {
  const base = (s: string) =>
    s
      .replace(/\s*[([|].*$/, "")
      .replace(/\s+/g, " ")
      .trim();
  const title = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1).toLowerCase() : s);
  const groups = new Map<string, string[]>();
  for (const name of list) {
    const first = base(name).split(" ")[0]?.toLowerCase() ?? "";
    const key = first || `__${name}`;
    const arr = groups.get(key) ?? [];
    arr.push(name);
    groups.set(key, arr);
  }
  const out: string[] = [];
  for (const [, members] of groups) {
    if (members.length < 3) {
      out.push(...members);
      continue;
    }
    const surnameCount = new Map<string, number>();
    for (const m of members) {
      const surname = base(m).split(" ")[1]?.toLowerCase();
      if (surname) surnameCount.set(surname, (surnameCount.get(surname) ?? 0) + 1);
    }
    let bestSurname = "";
    let bestCount = 0;
    for (const [s, c] of surnameCount) {
      if (c > bestCount) {
        bestSurname = s;
        bestCount = c;
      }
    }
    const first = base(members[0]!).split(" ")[0] ?? members[0]!;
    out.push(
      bestCount > 1 && bestSurname
        ? `${title(first)} ${title(bestSurname)}`
        : (members.map(base).sort((a, b) => b.length - a.length)[0] ?? members[0]!),
    );
  }
  return out;
}

function imgSrc(img: InfographicImage | Screenshot): string {
  if (img.imageUrl) {
    if (img.imageUrl.startsWith("http")) return img.imageUrl;
    return `${API_BASE}${img.imageUrl}`;
  }
  if (img.imageBase64) return `data:${img.mimeType};base64,${img.imageBase64}`;
  return "";
}

export function InternalSummary({
  summary,
  onSummaryChange,
  readOnly = false,
}: {
  summary: MeetingSummary;
  onSummaryChange: (s: MeetingSummary) => void;
  /** When previewing an older version, the summary is shown read-only: never
   * auto-generate/persist the custom block or accept edits, so viewing history
   * doesn't re-run Gemini or overwrite the live summary. */
  readOnly?: boolean;
}) {
  const raw = summary.internal;
  const s = {
    ...raw,
    wentWell: raw.wentWell ?? [],
    couldImprove: raw.couldImprove ?? [],
    upsell: (raw.upsell ?? []).map((u: any) =>
      typeof u === "string" ? { name: u, reason: "" } : u,
    ),
    risks: raw.risks ?? [],
    needs: raw.needs ?? { stated: [], actual: [] },
    actionItems: raw.actionItems ?? [],
    topMoments: (raw.topMoments ?? []).map((m: any) => ({
      t: m.t ?? "",
      type: m.type ?? m.label ?? "",
      quote: m.quote ?? m.note ?? "",
    })),
  };
  // Meeting language drives read-aloud voice + dictation recognition, so both
  // work in any language rather than defaulting to a single one.
  const meetingLang = summary.meeting.language;
  const dictateLang = meetingLang ? langMeta(meetingLang).bcp47 : undefined;
  const [items, setItems] = useState<ActionItem[]>(s.actionItems);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newWho, setNewWho] = useState("");
  const [newWhat, setNewWhat] = useState("");
  const [newDue, setNewDue] = useState("");
  const [wellOpen, setWellOpen] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);
  const [infImages, setInfImages] = useState<InfographicImage[]>([]);
  const [infGenerating, setInfGenerating] = useState(false);
  // Auto-generate one infographic on first view when a meeting produced none live.
  const autoInfoTriedRef = useRef(false);
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [expandedImg, setExpandedImg] = useState<InfographicImage | Screenshot | null>(null);
  const [diagramFullscreen, setDiagramFullscreen] = useState(false);
  const [expandedDiagram, setExpandedDiagram] = useState<{ title: string; mermaid: string } | null>(
    null,
  );
  const [forcingDiagram, setForcingDiagram] = useState(false);

  const toast = useToast();
  const user = useAuthStore((st) => st.user);
  const setUser = useAuthStore((st) => st.setUser);
  // Resolve the same question set that was used live for this meeting (role / meeting-type
  // preset), so the summary shows/regenerates the right questions — not just the defaults.
  const questions = resolveCustomQuestions(summary.meeting.customQuestionKey, user?.settings);

  // Custom summary state.
  const [refreshingCustom, setRefreshingCustom] = useState(false);
  const [editingAnswerIdx, setEditingAnswerIdx] = useState<number | null>(null);
  const [answerDraft, setAnswerDraft] = useState("");
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [questionsDraft, setQuestionsDraft] = useState<string[]>(questions);
  const [savingQuestions, setSavingQuestions] = useState(false);
  // Overview editing state.
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewDraft, setOverviewDraft] = useState("");
  const [companyDraft, setCompanyDraft] = useState("");
  const [savingOverview, setSavingOverview] = useState(false);
  const [editingParticipants, setEditingParticipants] = useState(false);
  const [participantsDraft, setParticipantsDraft] = useState<string[]>([]);
  const [savingParticipants, setSavingParticipants] = useState(false);
  const [editingUpsell, setEditingUpsell] = useState(false);
  const [upsellDraft, setUpsellDraft] = useState<UpsellOpportunity[]>([]);
  const [savingUpsell, setSavingUpsell] = useState(false);
  // Diagrams read straight from the live subcollection, so they show even when the
  // summary doc was persisted before a diagram was written (the "only after refresh"
  // bug). Merged with summary.internal.diagrams below.
  const [liveDiagrams, setLiveDiagrams] = useState<LiveDiagram[]>([]);

  const customSummary = summary.internal.customSummary;
  const customEnabled = user?.settings.customSummaryEnabled !== false;

  /** Answer + persist the custom questions SERVER-SIDE (Vertex + Apollo fallback), so
   * it works for every user without a personal Gemini key. Throws (WITHOUT persisting)
   * when nothing came back, so a transient failure doesn't lock the section empty. */
  async function runAndSaveCustom(qs: string[]): Promise<void> {
    const cleaned = qs.map((x) => x.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      const empty: CustomAnswer[] = [];
      await summaryApi.saveCustomSummary(summary.meetingId, empty);
      onSummaryChange({ ...summary, internal: { ...summary.internal, customSummary: empty } });
      return;
    }
    const answers = await summaryApi.generateCustomSummary(summary.meetingId, cleaned);
    if (answers.length === 0) {
      throw new Error("Couldn't generate the custom answers — please Refresh to try again.");
    }
    onSummaryChange({ ...summary, internal: { ...summary.internal, customSummary: answers } });
  }

  async function refreshCustomSummary() {
    if (readOnly) return;
    const edited = (customSummary ?? []).some((c) => c.edited);
    if (edited && !window.confirm("Refreshing will overwrite your edited answers. Continue?"))
      return;
    setRefreshingCustom(true);
    try {
      await runAndSaveCustom(questions);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setRefreshingCustom(false);
    }
  }

  // Stable signal of the resolved question set — so the effect below re-runs once the
  // user's settings (and thus their real questions) load, instead of firing early with
  // the defaults and never correcting itself.
  const questionsKey = questions
    .map((q) => q.trim())
    .filter(Boolean)
    .join("|");

  // Auto-generate the custom block on first view when it's missing. Answered
  // SERVER-SIDE now (no personal Gemini key needed), so it works for every user.
  // Re-runs once the profile / questions load (they resolve async).
  useEffect(() => {
    // Never auto-generate while previewing an older version — that would overwrite
    // the live summary just for viewing history.
    if (readOnly) return;
    // Wait for the user profile so `questions` are the real ones (settings-derived).
    if (!user) return;
    if (!customEnabled) return;
    // Regenerate when missing OR previously saved empty (a stale/failed run) while
    // questions exist — so meetings locked to "No custom answers yet" self-heal.
    if (customSummary !== undefined && customSummary.length > 0) return;
    if (questionsKey.length === 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    setRefreshingCustom(true);
    // The transcript flush can lag a moment after the meeting ends, so a first run
    // may yield nothing — retry a few times, and never persist an empty result.
    const attempt = async () => {
      if (cancelled) return;
      try {
        await runAndSaveCustom(questions);
        if (!cancelled) setRefreshingCustom(false);
      } catch {
        if (cancelled) return;
        if (tries < 5) {
          tries += 1;
          timer = setTimeout(attempt, 3000);
        } else {
          setRefreshingCustom(false);
        }
      }
    };
    void attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    summary.meetingId,
    (customSummary?.length ?? 0) === 0,
    readOnly,
    user,
    customEnabled,
    questionsKey,
  ]);

  // Load diagrams straight from meetings/{id}/diagrams so they appear without a page
  // refresh, even if the persisted summary doc missed one (it's merged in below).
  // Skipped while previewing an older version — that view should show the version's own.
  // If none are found yet, re-poll a few times with backoff: a diagram write can lag
  // the summary (generated right at meeting end), so this lets it appear on its own.
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const alreadyHaveEmbedded = (summary.internal.diagrams ?? []).length > 0;

    const poll = () => {
      summaryApi
        .listDiagrams(summary.meetingId)
        .then((items) => {
          if (cancelled) return;
          setLiveDiagrams(items);
          tries++;
          // Stop once we have something (or the summary already embeds a diagram),
          // else keep checking a handful of times for a lagging write.
          const haveDiagram = items.length > 0 || alreadyHaveEmbedded;
          if (!haveDiagram && tries < 6) {
            timer = setTimeout(poll, 5000);
          }
        })
        .catch(() => {
          if (cancelled) return;
          tries++;
          if (tries < 6) timer = setTimeout(poll, 5000);
        });
    };
    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [summary.meetingId, readOnly, summary.internal.diagrams]);

  // Diagrams to render: the summary doc's, plus any from the live subcollection not
  // already present (deduped by id), sorted by their generation index.
  const mergedDiagrams: LiveDiagram[] = (() => {
    const byId = new Map<string, LiveDiagram>();
    for (const d of summary.internal.diagrams ?? []) byId.set(d.id, d);
    for (const d of liveDiagrams) if (!byId.has(d.id)) byId.set(d.id, d);
    return [...byId.values()].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  })();

  const hasAnyDiagram =
    mergedDiagrams.length > 0 || !!summary.internal.architectureDiagram?.mermaid;

  // The model judged there was nothing structural to draw on this meeting. Offer a
  // one-click "Generate anyway" that re-runs with the technical (force-a-diagram) path.
  async function forceDiagram() {
    if (readOnly || forcingDiagram) return;
    setForcingDiagram(true);
    try {
      const fresh = await summaryApi.regenerateSummary(summary.meetingId, { technical: true });
      onSummaryChange(fresh);
      const drew =
        !!fresh.internal.architectureDiagram?.mermaid || (fresh.internal.diagrams ?? []).length > 0;
      toast.push(
        drew
          ? { tone: "success", message: "Diagram generated" }
          : { tone: "info", message: "Still nothing structural to diagram on this meeting" },
      );
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setForcingDiagram(false);
    }
  }

  function saveAnswerEdit(idx: number) {
    if (readOnly) return;
    const next = (customSummary ?? []).map((c, i) =>
      i === idx ? { ...c, answer: answerDraft.trim(), edited: true } : c,
    );
    setEditingAnswerIdx(null);
    setAnswerDraft("");
    onSummaryChange({ ...summary, internal: { ...summary.internal, customSummary: next } });
    void summaryApi.saveCustomSummary(summary.meetingId, next).catch((e) => {
      toast.push({ tone: "error", message: (e as Error).message });
    });
  }

  async function saveQuestions() {
    if (readOnly) return;
    const cleaned = questionsDraft.map((q) => q.trim()).filter(Boolean);
    setSavingQuestions(true);
    try {
      await authApi.updateSettings({ customQuestions: cleaned });
      if (user) {
        setUser({ ...user, settings: { ...user.settings, customQuestions: cleaned } });
      }
      setEditingQuestions(false);
      // Re-answer with the new questions (server-side — no personal key needed).
      setRefreshingCustom(true);
      await runAndSaveCustom(cleaned).finally(() => setRefreshingCustom(false));
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setSavingQuestions(false);
    }
  }

  async function saveOverviewText() {
    if (readOnly) return;
    setSavingOverview(true);
    try {
      const updated = await summaryApi.updateOverview(summary.meetingId, {
        summary: overviewDraft,
        companyName: companyDraft.trim(),
      });
      onSummaryChange(updated);
      setEditingOverview(false);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setSavingOverview(false);
    }
  }

  async function saveParticipants() {
    if (readOnly) return;
    setSavingParticipants(true);
    try {
      const cleaned = participantsDraft.map((a) => a.trim()).filter(Boolean);
      const updated = await summaryApi.updateOverview(summary.meetingId, { participants: cleaned });
      onSummaryChange(updated);
      setEditingParticipants(false);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setSavingParticipants(false);
    }
  }

  async function saveUpsell() {
    if (readOnly) return;
    setSavingUpsell(true);
    try {
      const cleaned = upsellDraft
        .map((u) => ({
          name: u.name.trim(),
          reason: u.reason.trim(),
          ...(u.estimatedMonthlyArr !== undefined
            ? { estimatedMonthlyArr: u.estimatedMonthlyArr }
            : {}),
        }))
        .filter((u) => u.name || u.reason);
      const updated = await summaryApi.updateOverview(summary.meetingId, { upsell: cleaned });
      onSummaryChange(updated);
      setEditingUpsell(false);
    } catch (e) {
      toast.push({ tone: "error", message: (e as Error).message });
    } finally {
      setSavingUpsell(false);
    }
  }

  useEffect(() => {
    // Load via the API (server-read) so infographics show even when the browser blocks
    // client-side Firestore — the same reason the transcript is API-served.
    summaryApi
      .listInfographics(summary.meetingId)
      .then((imgs) => {
        setInfImages(imgs);
        // If the meeting produced no infographic live, generate one now (once) so the
        // summary always has a visual — owner only, needs a Gemini key.
        if (imgs.length === 0 && !readOnly && hasGeminiKey() && !autoInfoTriedRef.current) {
          autoInfoTriedRef.current = true;
          if (summary.internal.overview?.summary?.trim() || (summary.internal.wentWell?.length ?? 0) > 0) {
            void generateInfographic(true);
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary.meetingId]);

  async function generateInfographic(isAuto = false) {
    if (readOnly || infGenerating) return;
    const key = await ensureGeminiKey();
    if (!key && !hasGeminiKey()) return;
    setInfGenerating(true);
    try {
      const img = await summaryApi.generateInfographic(summary.meetingId);
      setInfImages((prev) => [img, ...prev]);
    } catch (e) {
      if (!isAuto) {
        toast.push({ tone: "error", message: (e as Error).message });
      }
    } finally {
      setInfGenerating(false);
    }
  }

  useEffect(() => {
    // Load via the API (robust to blocked client Firestore).
    summaryApi
      .listScreenshots(summary.meetingId)
      .then((ss) => setScreenshots(ss))
      .catch(() => {});
  }, [summary.meetingId]);

  function persist(next: ActionItem[]) {
    if (readOnly) return;
    setItems(next);
    onSummaryChange({ ...summary, internal: { ...summary.internal, actionItems: next } });
    void summaryApi.updateActionItems(summary.meetingId, next).catch(() => {});
  }

  function toggleDone(id: string) {
    persist(items.map((a) => (a.id === id ? { ...a, done: !a.done } : a)));
  }

  function startEdit(a: ActionItem) {
    setEditingId(a.id);
    setEditDraft(a.what);
  }

  function commitEdit() {
    if (!editingId) return;
    const text = editDraft.trim();
    if (!text) {
      setEditingId(null);
      return;
    }
    persist(items.map((a) => (a.id === editingId ? { ...a, what: text } : a)));
    setEditingId(null);
    setEditDraft("");
  }

  function removeItem(id: string) {
    persist(items.filter((a) => a.id !== id));
  }

  function commitAdd() {
    const what = newWhat.trim();
    if (!what) {
      setAddingNew(false);
      return;
    }
    const item: ActionItem = {
      id: crypto.randomUUID(),
      who: newWho.trim() || "Rep",
      what,
      due: newDue || new Date().toISOString().slice(0, 10),
      done: false,
    };
    persist([...items, item]);
    setNewWho("");
    setNewWhat("");
    setNewDue("");
    setAddingNew(false);
  }

  const overview = summary.internal.overview;
  const hasOverview =
    !!overview &&
    (!!overview.summary ||
      (overview.whatWasDone?.length ?? 0) > 0 ||
      !!overview.companyName ||
      (overview.attendees?.length ?? 0) > 0 ||
      !!overview.companyBrief);

  return (
    <div className="sum-grid">
      <main className="sum-main">
        {hasOverview && overview && (
          <section className="sum-card">
            <div className="sum-card-head">
              <h3 className="sum-h3">
                <span className="dot" style={{ background: "var(--gc-blue)" }} /> Meeting summary
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {overview.companyName && (
                  <span className="sum-meta" dir="auto">
                    {overview.companyName}
                  </span>
                )}
                {!editingOverview && (
                  <>
                    {overview.summary && (
                      <ReadAloudButton
                        className="ghost-btn"
                        getText={() => overview.summary ?? ""}
                        lang={meetingLang}
                      />
                    )}
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => {
                        setOverviewDraft(overview.summary ?? "");
                        setCompanyDraft(overview.companyName ?? "");
                        setEditingOverview(true);
                      }}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>

            {editingOverview ? (
              <div style={{ padding: "0 16px 12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "var(--text-3)",
                    margin: "0 0 4px",
                  }}
                >
                  Company
                </label>
                <input
                  dir="auto"
                  value={companyDraft}
                  onChange={(e) => setCompanyDraft(e.target.value)}
                  placeholder="Company name (correct the auto-detected one if wrong)"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    fontSize: 14,
                    color: "var(--text-1)",
                    background: "var(--surface)",
                    marginBottom: 8,
                  }}
                />
                <textarea
                  dir="auto"
                  value={overviewDraft}
                  onChange={(e) => setOverviewDraft(e.target.value)}
                  rows={8}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    fontSize: 14,
                    color: "var(--text-1)",
                    background: "var(--surface)",
                    lineHeight: 1.5,
                    resize: "vertical",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                  {overviewDraft.trim() && (
                    <ReadAloudButton
                      className="ghost-btn"
                      getText={() => overviewDraft}
                      lang={meetingLang}
                    />
                  )}
                  <DictateButton
                    label="Dictate"
                    lang={dictateLang}
                    onText={(t) => setOverviewDraft((d) => `${d}${d ? " " : ""}${t}`)}
                  />
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => setEditingOverview(false)}
                    disabled={savingOverview}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pill-btn primary"
                    onClick={saveOverviewText}
                    disabled={savingOverview}
                  >
                    {savingOverview ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              overview.summary && (
                <div
                  dir="auto"
                  style={{
                    padding: "0 16px 12px",
                    fontSize: 14,
                    color: "var(--text-1)",
                    lineHeight: 1.5,
                  }}
                >
                  {overview.summary}
                </div>
              )
            )}

            {overview.whatWasDone && overview.whatWasDone.length > 0 && (
              <>
                <div
                  style={{
                    padding: "0 16px 4px",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    color: "var(--text-3)",
                  }}
                >
                  WHAT WAS DONE
                </div>
                <ul className="sum-list">
                  {overview.whatWasDone.map((x, i) => (
                    <li key={i} dir="auto">
                      {x}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(overview.companyBrief || overview.investmentStatus) && (
              <div
                style={{
                  margin: "12px 16px 4px",
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "var(--bg-2)",
                  border: "1px solid var(--border, rgba(0,0,0,0.08))",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    color: "var(--gc-blue)",
                    marginBottom: 4,
                  }}
                >
                  ABOUT {overview.companyName ? overview.companyName.toUpperCase() : "THE COMPANY"}
                </div>
                {overview.companyBrief && (
                  <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
                    {overview.companyBrief}
                  </div>
                )}
                {overview.investmentStatus && (
                  <div style={{ fontSize: 13, color: "var(--text-1)", marginTop: 6 }}>
                    <span style={{ fontWeight: 600 }}>Investment: </span>
                    {overview.investmentStatus}
                  </div>
                )}
                {overview.companySources && overview.companySources.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      fontSize: 11,
                    }}
                  >
                    {overview.companySources.map((src, i) => (
                      <a
                        key={i}
                        href={safeHref(src.href)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "var(--gc-blue)" }}
                        title={src.title}
                      >
                        {src.source || src.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {customEnabled && (
          <section className="sum-card" style={{ order: 2 }}>
            <div className="sum-card-head">
              <h3 className="sum-h3">
                <Spark size={14} /> Custom summary
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => {
                    setQuestionsDraft(questions);
                    setEditingQuestions((v) => !v);
                  }}
                >
                  <Settings size={14} /> Edit questions
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={refreshCustomSummary}
                    disabled={refreshingCustom}
                  >
                    <Spark size={14} /> {refreshingCustom ? "Refreshing…" : "Refresh"}
                  </button>
                )}
              </div>
            </div>

            {editingQuestions && (
              <div style={{ padding: "0 16px 12px" }}>
                <CustomQuestionsEditor questions={questionsDraft} onChange={setQuestionsDraft} />
                <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => setEditingQuestions(false)}
                    disabled={savingQuestions}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pill-btn primary"
                    onClick={saveQuestions}
                    disabled={savingQuestions}
                  >
                    {savingQuestions ? "Saving…" : "Save & re-answer"}
                  </button>
                </div>
              </div>
            )}

            {refreshingCustom && (customSummary ?? []).length === 0 ? (
              <div style={{ padding: "0 16px 14px", color: "var(--text-3)", fontSize: 13 }}>
                Answering your custom questions…
              </div>
            ) : (customSummary ?? []).length === 0 ? (
              <div style={{ padding: "0 16px 14px", color: "var(--text-3)", fontSize: 13 }}>
                No custom answers yet. Click <strong>Refresh</strong> to answer your questions from
                the transcript.
              </div>
            ) : (
              <div
                style={{ padding: "0 16px 8px", display: "flex", flexDirection: "column", gap: 14 }}
              >
                {(customSummary ?? []).map((c, i) => (
                  <div key={i}>
                    <div
                      dir="auto"
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "var(--text-1)",
                        marginBottom: 3,
                      }}
                    >
                      {c.question}
                    </div>
                    {editingAnswerIdx === i ? (
                      <div>
                        <textarea
                          dir="auto"
                          value={answerDraft}
                          autoFocus
                          rows={3}
                          onChange={(e) => setAnswerDraft(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid var(--border)",
                            fontSize: 13,
                            color: "var(--text-1)",
                            background: "var(--surface)",
                            lineHeight: 1.5,
                            resize: "vertical",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 6,
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            className="ghost-btn"
                            onClick={() => setEditingAnswerIdx(null)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="pill-btn primary"
                            onClick={() => saveAnswerEdit(i)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        dir="auto"
                        onClick={() => {
                          setEditingAnswerIdx(i);
                          setAnswerDraft(c.answer);
                        }}
                        title="Click to edit"
                        style={{
                          fontSize: 13,
                          color:
                            c.source === "not-found" && !c.edited
                              ? "var(--text-4)"
                              : "var(--text-2)",
                          lineHeight: 1.5,
                          cursor: "pointer",
                        }}
                      >
                        {c.answer}
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}>
                      <SourceBadge source={c.source} edited={c.edited} />
                    </div>
                    {(c.source === "web" || c.source === "apollo") &&
                      c.sources &&
                      c.sources.length > 0 && (
                        <div
                          style={{
                            marginTop: 4,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            fontSize: 11,
                          }}
                        >
                          {c.sources.map((src, j) => (
                            <a
                              key={j}
                              href={safeHref(src.href)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "var(--gc-blue)" }}
                              title={src.title}
                            >
                              {src.source || src.title}
                            </a>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">At a glance</h3>
            <span className="sum-meta mono">Confidence {Math.round(s.confidence * 100)}%</span>
          </div>
          <div className="glance-row">
            <div className="glance-tile">
              <div className="glance-label">Rating</div>
              <div
                className="glance-val"
                style={{
                  color:
                    s.score >= 70
                      ? "var(--gc-green)"
                      : s.score >= 40
                        ? "var(--gc-yellow)"
                        : "var(--gc-red, #b00020)",
                }}
              >
                {s.score >= 70 ? "High" : s.score >= 40 ? "Medium" : "Low"}
              </div>
            </div>
            <div className="glance-tile">
              <div className="glance-label">Deal health</div>
              <div
                className="glance-val"
                style={{
                  color:
                    s.health === "hot"
                      ? "var(--gc-green)"
                      : s.health === "warm"
                        ? "var(--gc-yellow)"
                        : "var(--gc-red, #b00020)",
                }}
              >
                {s.health[0]?.toUpperCase()}
                {s.health.slice(1)}
              </div>
              {s.topMoments[0] && (
                <div className="glance-foot">
                  {s.topMoments[0].type} at {s.topMoments[0].t}
                </div>
              )}
            </div>
            <div className="glance-tile">
              <div className="glance-label">Key topics</div>
              <div className="glance-val" style={{ fontSize: 16 }}>
                {s.topMoments.length > 0
                  ? [...new Set(s.topMoments.map((m) => m.type))].slice(0, 3).join(", ")
                  : "—"}
              </div>
              {s.topMoments.length > 0 && (
                <div className="glance-foot">
                  {s.topMoments.length} key moment{s.topMoments.length !== 1 ? "s" : ""}
                </div>
              )}
            </div>
            <div className="glance-tile">
              <div className="glance-label">Open tasks</div>
              <div
                className="glance-val"
                style={{
                  color:
                    items.filter((a) => !a.done).length > 0
                      ? "var(--gc-yellow)"
                      : "var(--gc-green)",
                }}
              >
                {items.filter((a) => !a.done).length}
                <span>/{items.length}</span>
              </div>
              <div className="glance-foot">{items.filter((a) => a.done).length} completed</div>
            </div>
          </div>
        </section>

        <section className="sum-card">
          <div
            className="sum-card-head"
            style={{ cursor: "pointer" }}
            onClick={() => setWellOpen(!wellOpen)}
          >
            <h3 className="sum-h3">
              <span className="dot" style={{ background: "var(--gc-green)" }} /> What went well
              <span className="sum-meta" style={{ marginLeft: 8, fontWeight: 400 }}>
                {s.wentWell.length}
              </span>
            </h3>
            <Chev
              size={14}
              style={{
                transform: wellOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.15s",
              }}
            />
          </div>
          {s.wentWell.length > 0 && (
            <div
              dir="auto"
              style={{
                padding: "0 16px 12px",
                fontWeight: 600,
                fontSize: 14,
                color: "var(--text-1)",
              }}
            >
              {s.wentWell[0]}
            </div>
          )}
          {wellOpen && s.wentWell.length > 1 && (
            <ul className="sum-list">
              {s.wentWell.slice(1).map((x, i) => (
                <li key={i} dir="auto">
                  {x}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="sum-card">
          <div
            className="sum-card-head"
            style={{ cursor: "pointer" }}
            onClick={() => setImproveOpen(!improveOpen)}
          >
            <h3 className="sum-h3">
              <span className="dot" style={{ background: "var(--gc-yellow)" }} /> Where to push
              deeper
              <span className="sum-meta" style={{ marginLeft: 8, fontWeight: 400 }}>
                {s.couldImprove.length}
              </span>
            </h3>
            <Chev
              size={14}
              style={{
                transform: improveOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.15s",
              }}
            />
          </div>
          {s.couldImprove.length > 0 && (
            <div
              dir="auto"
              style={{
                padding: "0 16px 12px",
                fontWeight: 600,
                fontSize: 14,
                color: "var(--text-1)",
              }}
            >
              {s.couldImprove[0]}
            </div>
          )}
          {improveOpen && s.couldImprove.length > 1 && (
            <ul className="sum-list">
              {s.couldImprove.slice(1).map((x, i) => (
                <li key={i} dir="auto">
                  {x}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">Stated vs. actual needs</h3>
          </div>
          <div className="needs-row">
            <div className="needs-col">
              <div className="needs-kicker">CLIENT STATED</div>
              {(s.needs.stated ?? []).map((n, i) => (
                <div key={i} className="needs-item needs-stated" dir="auto">
                  {n}
                </div>
              ))}
            </div>
            <div className="needs-arrow">›</div>
            <div className="needs-col">
              <div className="needs-kicker" style={{ color: "var(--gc-blue)" }}>
                COACH INFERRED
              </div>
              {(s.needs.actual ?? []).map((n, i) => (
                <div key={i} className="needs-item needs-actual" dir="auto">
                  {n}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">Action items</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className="sum-meta">{items.filter((a) => !a.done).length} open</span>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setAddingNew(true);
                  setNewWho("");
                  setNewWhat("");
                  setNewDue("");
                }}
              >
                + Add
              </button>
            </div>
          </div>
          <ul className="action-list">
            {items.map((a) => (
              <li key={a.id} className={a.done ? "done" : ""}>
                <input type="checkbox" checked={a.done} onChange={() => toggleDone(a.id)} />
                <div className="action-who">{a.who}</div>
                {editingId === a.id ? (
                  <div className="action-edit-wrap">
                    <input
                      type="text"
                      className="action-edit-input"
                      value={editDraft}
                      autoFocus
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitEdit();
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={commitEdit}
                    />
                  </div>
                ) : (
                  <div
                    className="action-what"
                    onClick={() => startEdit(a)}
                    style={{ cursor: "pointer" }}
                    dir="auto"
                  >
                    {a.what}
                  </div>
                )}
                <div className="action-due mono">{a.due}</div>
                <div className="action-row-actions">
                  <button
                    type="button"
                    className="ghost-btn xs danger"
                    onClick={() => removeItem(a.id)}
                    title="Remove"
                  >
                    <Close size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {addingNew && (
            <div className="action-add-form">
              <input
                type="text"
                className="action-edit-input"
                placeholder="Who"
                value={newWho}
                onChange={(e) => setNewWho(e.target.value)}
                style={{ width: 70 }}
              />
              <input
                type="text"
                className="action-edit-input"
                placeholder="What needs to be done"
                value={newWhat}
                autoFocus
                onChange={(e) => setNewWhat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitAdd();
                  if (e.key === "Escape") setAddingNew(false);
                }}
              />
              <input
                type="date"
                className="action-edit-input"
                value={newDue}
                onChange={(e) => setNewDue(e.target.value)}
                style={{ width: 130 }}
              />
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" className="icon-btn xs" onClick={commitAdd} title="Save">
                  <Check size={14} />
                </button>
                <button
                  type="button"
                  className="icon-btn xs"
                  onClick={() => setAddingNew(false)}
                  title="Cancel"
                >
                  <Close size={14} />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Summary diagrams — ALL diagrams generated during the meeting (plus the
            post-hoc architecture diagram), for every meeting when any were drawn. */}
        {mergedDiagrams.length > 0
          ? mergedDiagrams.map((d) => {
              const dTitle = d.title || (d.kind === "flowchart" ? "Flowchart" : "Architecture");
              return (
                <section key={d.id} className="sum-card" style={{ order: 2 }}>
                  <div className="sum-card-head">
                    <h3 className="sum-h3">
                      <Image size={14} /> {dTitle}
                    </h3>
                    <button
                      type="button"
                      className="icon-btn xs"
                      title="Expand to full screen"
                      onClick={() => setExpandedDiagram({ title: dTitle, mermaid: d.mermaid })}
                    >
                      <Expand size={14} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedDiagram({ title: dTitle, mermaid: d.mermaid })}
                    style={{
                      display: "block",
                      width: "100%",
                      border: "none",
                      background: "none",
                      padding: "8px 16px 16px",
                      cursor: "zoom-in",
                    }}
                    title="Click to expand"
                  >
                    <MermaidDiagram chart={d.mermaid} />
                  </button>
                </section>
              );
            })
          : summary.internal.architectureDiagram?.mermaid && (
              <section className="sum-card" style={{ order: 2 }}>
                <div className="sum-card-head">
                  <h3 className="sum-h3">
                    <Image size={14} />{" "}
                    {summary.internal.architectureDiagram.title || "Architecture"}
                  </h3>
                  <button
                    type="button"
                    className="icon-btn xs"
                    title="Expand to full screen"
                    onClick={() => setDiagramFullscreen(true)}
                  >
                    <Expand size={14} />
                  </button>
                </div>
                <div style={{ padding: "8px 16px 16px" }}>
                  <MermaidDiagram chart={summary.internal.architectureDiagram.mermaid} zoomable />
                </div>
              </section>
            )}

        {/* The model found nothing structural to diagram (a discovery/QBR/1:1/strategy
            call). Say so explicitly — instead of silently showing no diagram — and let
            the owner force one anyway with the technical (force-a-diagram) re-run. */}
        {!hasAnyDiagram && summary.internal.diagramSkipped && !readOnly && (
          <section className="sum-card" style={{ order: 2 }}>
            <div className="sum-card-head">
              <h3 className="sum-h3">
                <Image size={14} /> Diagram
              </h3>
            </div>
            <div style={{ padding: "8px 16px 16px", color: "var(--text-2)", fontSize: 13 }}>
              <p style={{ margin: "0 0 10px" }}>
                SellSynq didn't find a concrete architecture, flow, or solution to visualize on
                this meeting, so no diagram was drawn. You can generate one anyway.
              </p>
              <button
                type="button"
                className="ghost-btn"
                disabled={forcingDiagram}
                onClick={() => void forceDiagram()}
              >
                <Image size={14} /> {forcingDiagram ? "Generating…" : "Generate anyway"}
              </button>
            </div>
          </section>
        )}

        {expandedDiagram && (
          <div className="ig-lightbox" onClick={() => setExpandedDiagram(null)}>
            <button
              type="button"
              className="ig-lightbox-close"
              onClick={() => setExpandedDiagram(null)}
              aria-label="Close"
            >
              <Close size={20} />
            </button>
            <div className="diagram-fullscreen" onClick={(e) => e.stopPropagation()}>
              <div className="diagram-fullscreen-title">
                {expandedDiagram.title}
                <button
                  type="button"
                  className="ghost-btn"
                  style={{ marginLeft: 12 }}
                  title="Open this diagram in a new browser tab"
                  onClick={() =>
                    void openDiagramInNewTab(expandedDiagram.title, expandedDiagram.mermaid)
                  }
                >
                  <Expand size={14} /> Open in new tab
                </button>
              </div>
              <MermaidDiagram chart={expandedDiagram.mermaid} zoomable />
            </div>
          </div>
        )}

        {diagramFullscreen && summary.internal.architectureDiagram?.mermaid && (
          <div className="ig-lightbox" onClick={() => setDiagramFullscreen(false)}>
            <button
              type="button"
              className="ig-lightbox-close"
              onClick={() => setDiagramFullscreen(false)}
              aria-label="Close"
            >
              <Close size={20} />
            </button>
            <div className="diagram-fullscreen" onClick={(e) => e.stopPropagation()}>
              <div className="diagram-fullscreen-title">
                {summary.internal.architectureDiagram.title || "Architecture"}
                <button
                  type="button"
                  className="ghost-btn"
                  style={{ marginLeft: 12 }}
                  title="Open this diagram in a new browser tab"
                  onClick={() =>
                    void openDiagramInNewTab(
                      summary.internal.architectureDiagram?.title || "Architecture",
                      summary.internal.architectureDiagram?.mermaid || "",
                    )
                  }
                >
                  <Expand size={14} /> Open in new tab
                </button>
              </div>
              <MermaidDiagram chart={summary.internal.architectureDiagram.mermaid} zoomable />
            </div>
          </div>
        )}

        {(!readOnly || infImages.length > 0) && (
          <section className="sum-card" style={{ order: 3 }}>
            <div className="sum-card-head">
              <h3 className="sum-h3">
                <Image size={14} /> Meeting infographics
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {infImages.length > 0 && (
                  <span className="sum-meta">{infImages.length} generated</span>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => void generateInfographic()}
                    disabled={infGenerating}
                  >
                    <Spark size={14} /> {infGenerating ? "Generating…" : "Generate infographic"}
                  </button>
                )}
              </div>
            </div>
            {infImages.length === 0 && (
              <div style={{ padding: "0 16px 14px", color: "var(--text-3)", fontSize: 13 }}>
                {infGenerating
                  ? "Generating an infographic from the meeting…"
                  : "No infographic yet. Click Generate infographic to create one from this meeting."}
              </div>
            )}
            <div className="sum-infographic-grid">
              {infImages.map((img) => (
                <div
                  key={img.id}
                  className="sum-infographic-item"
                  onClick={() => setExpandedImg(img)}
                >
                  <AuthImage
                    src={imgSrc(img)}
                    alt={img.prompt?.slice(0, 80) ?? "Infographic"}
                    className="sum-infographic-img"
                  />
                  <div className="sum-infographic-meta">
                    <span className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                      {new Date(img.generatedAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <button type="button" className="icon-btn xs" title="Expand">
                      <Expand size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {screenshots.length > 0 && (
          <section className="sum-card" style={{ order: 3 }}>
            <div className="sum-card-head">
              <h3 className="sum-h3">
                <Monitor size={14} /> Screenshots
              </h3>
              <span className="sum-meta">{screenshots.length} captured</span>
            </div>
            <div className="sum-infographic-grid">
              {screenshots.map((ss) => (
                <div
                  key={ss.id}
                  className="sum-infographic-item"
                  onClick={() => setExpandedImg(ss)}
                >
                  <AuthImage
                    src={imgSrc(ss)}
                    alt={`Screenshot at ${new Date(ss.capturedAt).toLocaleTimeString()}`}
                    className="sum-infographic-img"
                  />
                  <div className="sum-infographic-meta">
                    <span className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                      {new Date(ss.capturedAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {ss.analysisHints && ss.analysisHints.length > 0 && (
                      <span style={{ fontSize: 10, color: "var(--gc-blue)", marginLeft: 4 }}>
                        {ss.analysisHints.length} hint{ss.analysisHints.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    <button type="button" className="icon-btn xs" title="Expand">
                      <Expand size={12} />
                    </button>
                  </div>
                  {ss.analysisHints && ss.analysisHints.length > 0 && (
                    <div style={{ padding: "4px 8px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {ss.analysisHints.slice(0, 3).map((h) => (
                        <span
                          key={h.id}
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: "var(--bg-2)",
                            color: "var(--text-2)",
                          }}
                        >
                          {h.summary.slice(0, 60)}
                          {h.summary.length > 60 ? "..." : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {expandedImg && (
          <div className="ig-lightbox" onClick={() => setExpandedImg(null)}>
            <button
              type="button"
              className="ig-lightbox-close"
              onClick={() => setExpandedImg(null)}
              aria-label="Close"
            >
              <Close size={20} />
            </button>
            <AuthImage
              src={imgSrc(expandedImg)}
              alt={
                "prompt" in expandedImg
                  ? (expandedImg.prompt?.slice(0, 80) ?? "Infographic")
                  : "Screenshot"
              }
              className="ig-lightbox-img"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </main>

      <aside className="sum-aside">
        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">
              <Trend size={14} /> Upsell opportunities
            </h3>
            {!editingUpsell && (
              <button
                type="button"
                className="ghost-btn xs"
                onClick={() => {
                  setUpsellDraft(s.upsell.length > 0 ? s.upsell : [{ name: "", reason: "" }]);
                  setEditingUpsell(true);
                }}
              >
                Edit
              </button>
            )}
          </div>
          {editingUpsell ? (
            <div
              style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 10 }}
            >
              {upsellDraft.map((u, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <input
                    dir="auto"
                    placeholder="Opportunity"
                    value={u.name}
                    style={{ width: "100%" }}
                    onChange={(e) =>
                      setUpsellDraft((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                      )
                    }
                  />
                  <textarea
                    dir="auto"
                    rows={2}
                    placeholder="Why / detail"
                    value={u.reason}
                    style={{ width: "100%", resize: "vertical" }}
                    onChange={(e) =>
                      setUpsellDraft((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="ghost-btn xs"
                    onClick={() => setUpsellDraft((prev) => prev.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="ghost-btn xs"
                onClick={() => setUpsellDraft((prev) => [...prev, { name: "", reason: "" }])}
              >
                <Plus size={12} /> Add opportunity
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="pill-btn primary"
                  disabled={savingUpsell}
                  onClick={saveUpsell}
                >
                  {savingUpsell ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  className="pill-btn"
                  disabled={savingUpsell}
                  onClick={() => setEditingUpsell(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            s.upsell.map((u, i) => (
              <div key={i} className="upsell-item">
                <div className="upsell-name">{u.name}</div>
                <div className="upsell-reason">{u.reason}</div>
              </div>
            ))
          )}
        </section>

        <MeetingChat meetingId={summary.meetingId} />

        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">
              <Users size={14} /> Participants
            </h3>
            {!editingParticipants && (
              <button
                type="button"
                className="ghost-btn xs"
                onClick={() => {
                  const seed =
                    summary.meeting.participants && summary.meeting.participants.length > 0
                      ? summary.meeting.participants
                      : (summary.internal.overview?.attendees ?? []);
                  setParticipantsDraft(dedupeParticipantStrings(seed));
                  setEditingParticipants(true);
                }}
              >
                Edit
              </button>
            )}
          </div>

          {editingParticipants ? (
            <div
              style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 6 }}
            >
              {participantsDraft.map((p, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    dir="auto"
                    value={p}
                    placeholder="Name (role / company)"
                    onChange={(e) =>
                      setParticipantsDraft((d) => d.map((x, j) => (j === i ? e.target.value : x)))
                    }
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="icon-btn xs danger"
                    title="Remove"
                    onClick={() => setParticipantsDraft((d) => d.filter((_, j) => j !== i))}
                  >
                    <Close size={12} />
                  </button>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 4,
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  className="ghost-btn xs"
                  onClick={() => setParticipantsDraft((d) => [...d, ""])}
                >
                  <Plus size={12} /> Add person
                </button>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => setEditingParticipants(false)}
                    disabled={savingParticipants}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pill-btn primary"
                    onClick={saveParticipants}
                    disabled={savingParticipants}
                  >
                    {savingParticipants ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          ) : summary.meeting.participants && summary.meeting.participants.length > 0 ? (
            <ul className="sum-list">
              {dedupeParticipantStrings(summary.meeting.participants).map((p, i) => (
                <li key={i} dir="auto">
                  {p}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ padding: "0 16px 12px", fontSize: 13, color: "var(--text-4)" }}>
              No participants yet — click Edit to add them.
            </div>
          )}
        </section>

        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">
              <Alert size={14} /> Risks
            </h3>
          </div>
          <ul className="risk-list">
            {s.risks.map((r, i) => (
              <li key={i}>
                <span className="risk-dot" />
                {r}
              </li>
            ))}
          </ul>
        </section>

        <section className="sum-card">
          <div className="sum-card-head">
            <h3 className="sum-h3">Top moments</h3>
            <span className="sum-meta">{s.topMoments.length} detected</span>
          </div>
          {s.topMoments.length === 0 ? (
            <div style={{ padding: "12px 16px", color: "var(--text-4)", fontSize: 13 }}>
              No key moments detected in this call.
            </div>
          ) : (
            <div className="moment-timeline">
              {s.topMoments.map((m, i) => {
                const typeColor =
                  m.type.toLowerCase().includes("objection") ||
                  m.type.toLowerCase().includes("risk")
                    ? "var(--gc-red)"
                    : m.type.toLowerCase().includes("positive") ||
                        m.type.toLowerCase().includes("agreement")
                      ? "var(--gc-green)"
                      : m.type.toLowerCase().includes("question")
                        ? "var(--gc-yellow)"
                        : "var(--gc-blue)";
                return (
                  <div key={i} className="moment-item">
                    <div className="moment-rail">
                      <div className="moment-dot" style={{ background: typeColor }} />
                      {i < s.topMoments.length - 1 && <div className="moment-line" />}
                    </div>
                    <div className="moment-content">
                      <div className="moment-head">
                        <span className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                          {m.t}
                        </span>
                        <span
                          style={{ fontSize: 12, fontWeight: 600, color: typeColor, marginLeft: 8 }}
                        >
                          {m.type}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>
                        "{m.quote}"
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {s.questionsAsked && s.questionsAsked.length > 0 && (
          <section className="sum-card">
            <div className="sum-card-head">
              <h3 className="sum-h3">Questions asked</h3>
              <span className="sum-meta">{s.questionsAsked.length} Q&A</span>
            </div>
            <ul className="sum-list">
              {s.questionsAsked.map((qa, i) => (
                <li key={i} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    {qa.urgent && (
                      <span
                        style={{
                          color: "var(--gc-red)",
                          marginRight: 6,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        URGENT
                      </span>
                    )}
                    Q: {qa.q}
                  </div>
                  <div style={{ color: "var(--text-2)", fontSize: 13 }}>{qa.a}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {s.actedHints && s.actedHints.length > 0 && (
          <section className="sum-card">
            <div className="sum-card-head">
              <h3 className="sum-h3">Hints you used</h3>
              <span className="sum-meta">{s.actedHints.length} acted on</span>
            </div>
            <ul className="sum-list">
              {s.actedHints.map((h, i) => (
                <li key={i}>
                  <b>{h.title}</b> — {h.summary}
                </li>
              ))}
            </ul>
          </section>
        )}
      </aside>
    </div>
  );
}

function SourceBadge({
  source,
  edited,
}: {
  source: "meeting" | "web" | "apollo" | "not-found";
  edited?: boolean;
}) {
  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 12,
  };
  if (edited) {
    return (
      <span style={{ ...base, background: "var(--bg-2)", color: "var(--text-3)" }}>
        Edited by you
      </span>
    );
  }
  if (source === "meeting") {
    return (
      <span
        style={{
          ...base,
          background: "var(--gc-green-50, #e6f4ea)",
          color: "var(--gc-green, #1e8e3e)",
        }}
      >
        <Check size={11} /> From the meeting
      </span>
    );
  }
  if (source === "web") {
    return (
      <span style={{ ...base, background: "var(--bg-2)", color: "var(--gc-blue)" }}>
        <Globe size={11} /> From the web · not said in the meeting
      </span>
    );
  }
  if (source === "apollo") {
    return (
      <span style={{ ...base, background: "var(--bg-2)", color: "var(--gc-blue)" }}>
        <Globe size={11} /> From Apollo.io · not said in the meeting
      </span>
    );
  }
  return (
    <span style={{ ...base, background: "var(--bg-2)", color: "var(--text-4)" }}>Not found</span>
  );
}
